import { updateConfigList, updateConfigListForMetric } from '@src/api/aiAlerts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CONFIG_LIST_KEY } from '../../metrics-panel-hz/hooks/alerts/alertsQueryKey.constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';

// Hook for general config list updates (invalidates only CONFIG_LIST_KEY)
export const useUpdateConfigList = (onMutateSuccess, onMutateError) => {
  const queryClient = useQueryClient();
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);

  // Choose the appropriate API function based on feature flag
  const mutationFn = enableDPICustomDimension ? updateConfigListForMetric : updateConfigList;

  return useMutation({
    mutationFn,
    onSuccess: () => {
      onMutateSuccess?.();
      // Invalidate general config list queries
      queryClient.invalidateQueries([CONFIG_LIST_KEY]);
    },
    onError: () => {
      onMutateError?.();
      queryClient.invalidateQueries([CONFIG_LIST_KEY]);
      throw new Error('Error updating custom dimensions');
    },
  });
};

// Hook for metric-specific config list updates (invalidates both CONFIG_LIST_KEY and metric-specific queries)
export const useUpdateConfigListForMetric = (
  onMutateSuccess,
  onMutateError,
  metrics: string[] = [],
) => {
  const queryClient = useQueryClient();
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);

  // Choose the appropriate API function based on feature flag
  const mutationFn = enableDPICustomDimension ? updateConfigListForMetric : updateConfigList;

  return useMutation({
    mutationFn,
    onSuccess: () => {
      onMutateSuccess?.();
      // Invalidate metric-specific query used in useFetchMetricConfig
      queryClient.invalidateQueries([CONFIG_LIST_KEY, JSON.stringify(metrics)]);
    },
    onError: () => {
      onMutateError?.();
      queryClient.invalidateQueries([CONFIG_LIST_KEY, JSON.stringify(metrics)]);

      throw new Error('Error updating custom dimensions for metric');
    },
  });
};
